import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {inProgress, isNullOrUndefined} from "../../../../common/utils";
import {createConditionTree} from "../../../../components/condition/utils/createConditionTree";
import {patchApiCallAttributes} from "../common/patchApiCallAttributes/patchApiCallAttributes";
import {PreviewMembershipActions} from "./model";

export const PreviewMembershipController = (
  userGroupUniqueName,
  addedPagination,
  stayingPagination,
  removedPagination
) => {
  const dispatch = useDispatch();

  const modifiedKeyCondition = useSelector(
    (rootState) => rootState.keyCondition.modifiedKeyCondition
  );

  const isSpecialConditionChecked = useSelector(
    (rootState) => rootState.keyCondition.isSpecialConditionChecked
  );

  const specialConditionNode = useSelector(
    (rootState) => rootState.keyCondition.specialConditionNode
  );

  const selectedUser = useSelector((rootState) => rootState.keyCondition.selectedUser);

  const selectSpecialUser = {
    key: selectedUser?.key,
    userName: selectedUser?.fullName,
    userEmployeeId: selectedUser?.employeeId,
    userEmail: selectedUser?.email,
    reason: specialConditionNode?.reason,
  };

  const columns = useSelector((rootState) => rootState.previewMembership.previewMembership.columns);

  const userGroupDetailApiProgress = useSelector((rootState) => {
    return rootState.userGroup.userGroupDetails[userGroupUniqueName]?.progress;
  });
  const loading = inProgress(userGroupDetailApiProgress);

  const userGroupDetails = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value
  );

  const action = useSelector((rootState) => rootState.keyCondition.action);
  const editingConditionId = useSelector((rootState) => rootState.keyCondition.editingConditionId);

  const excludedMembers =
    useSelector((rootState) => rootState.membersMatchingConditionList.excludedMembers);

  const isUserExcluded = (user) => excludedMembers.find((excludedMember) => excludedMember.userId === user.key)

  const rowClassNameFn = (user) => isUserExcluded(user) ? "member-excluded" : ""

  let userGroupIndividualInclusions = userGroupDetails?.individualInclusionMembers;

  const {value: addedValue, progress: addedProgress} = useSelector(
    (rootState) => rootState.previewMembership.previewMembership.added
  );
  const {value: stayingValue, progress: stayingProgress} = useSelector(
    (rootState) => rootState.previewMembership.previewMembership.staying
  );
  const {value: removedValue, progress: removedProgress} = useSelector(
    (rootState) => rootState.previewMembership.previewMembership.removed
  );

  const membershipConditionsTree = createConditionTree(userGroupDetails?.membershipCondition, null);

  let patchOperationPayload;
  if (loading === false && !isNullOrUndefined(action)) {
    let conditionRequest;
    if (isSpecialConditionChecked) {
      conditionRequest = createConditionTree(specialConditionNode, null);
    } else {
      conditionRequest = createConditionTree(modifiedKeyCondition, null);
    }
    patchOperationPayload = patchApiCallAttributes(
      action,
      membershipConditionsTree,
      conditionRequest,
      editingConditionId,
      excludedMembers
    );
  }

  useEffect(() => {
    if (loading === false && !isNullOrUndefined(action)) {
      dispatch(PreviewMembershipActions.setPatchMembershipRequest(patchOperationPayload));
    }
  }, [patchOperationPayload, loading, action]);

  useEffect(() => {
    if (loading === false && !isNullOrUndefined(action)) {
      dispatch(
        PreviewMembershipActions.getAddedPreviewMembers({
          userGroupUniqueName,
          page: addedPagination.page,
          size: addedPagination.size,
          payload: patchOperationPayload,
        })
      );
    }
  }, [addedPagination, loading, action]);

  useEffect(() => {
    if (loading === false && !isNullOrUndefined(action)) {
      dispatch(
        PreviewMembershipActions.getStayingPreviewMembers({
          userGroupUniqueName,
          page: stayingPagination.page,
          size: stayingPagination.size,
          payload: patchOperationPayload,
        })
      );
    }
  }, [stayingPagination, loading, action]);

  useEffect(() => {
    if (loading === false && !isNullOrUndefined(action)) {
      dispatch(
        PreviewMembershipActions.getRemovedPreviewMembers({
          userGroupUniqueName,
          page: removedPagination.page,
          size: removedPagination.size,
          payload: patchOperationPayload,
        })
      );
    }
  }, [removedPagination, loading, action]);

  return {
    isSpecialConditionChecked,
    selectSpecialUser,
    modifiedKeyCondition,
    columns,
    addedValue,
    stayingValue,
    removedValue,
    isLoadingForAdded: !!addedProgress,
    isLoadingForStaying: !!stayingProgress,
    isLoadingForRemoved: !!removedProgress,
    membershipConditionsTree,
    userGroupIndividualInclusions: userGroupIndividualInclusions,
    action,
    editingConditionId,
    loading,
    rowClassNameFn
  };
};
